import {useCallback, useEffect, useMemo, useState} from 'react';

import useSettingApi from '~/personalArea/components/Sections/Settings/useSettingApi';
import {ToggleName} from '~/shared/types/personalArea';
import {
  fetchUserNotificationsPreferences,
  updateMarketingSubscriptionPreferences,
  updateResReviewNotificationPreferences,
  updateUserNotificationsPreferences,
} from '~/shared/services/bffService/api';
import {CommunicationMethodName} from '~/shared/services/bffService/api/interfaces/payloads';
import {is401Error} from '~/shared/services/apiErrorService';
import {handleRefreshToken} from '~/shared/services/auth';

import {useSnackbar} from './Snackbar';

const updateEmailNotifications = (isEnabled: boolean) =>
  updateUserNotificationsPreferences({
    communicationMethod: CommunicationMethodName.EMAIL,
    enabled: isEnabled,
  });

const updateSmsNotifications = (isEnabled: boolean) =>
  updateUserNotificationsPreferences({
    communicationMethod: CommunicationMethodName.SMS,
    enabled: isEnabled,
  });

const updatePushNotifications = (isEnabled: boolean) =>
  updateUserNotificationsPreferences({
    communicationMethod: CommunicationMethodName.PUSH,
    enabled: isEnabled,
  });

const useSettings = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const {showSnackbar} = useSnackbar();

  const onPromotionsChangeSuccess = useCallback(() => {
    showSnackbar();
  }, [showSnackbar]);

  const {
    setIsActive: setIsPromotionsActive,
    isActive: isPromotionsActive,
    hasError: hasPromotionsError,
    handleChange: handlePromotionsChange,
  } = useSettingApi(ToggleName.PROMOTIONS, updateMarketingSubscriptionPreferences, {
    onSuccess: onPromotionsChangeSuccess,
  });
  const {
    setIsActive: setIsResReviewActive,
    isActive: isResReviewActive,
    hasError: hasResReviewError,
    handleChange: handleResReviewChange,
  } = useSettingApi(ToggleName.RES_REVIEW, updateResReviewNotificationPreferences);
  const {
    setIsActive: setIsEmailsActive,
    isActive: isEmailsActive,
    hasError: hasEmailsError,
    handleChange: handleEmailsChange,
  } = useSettingApi(CommunicationMethodName.EMAIL, updateEmailNotifications);
  const {
    setIsActive: setIsSmsActive,
    isActive: isSmsActive,
    hasError: hasSmsError,
    handleChange: handleSmsChange,
  } = useSettingApi(CommunicationMethodName.SMS, updateSmsNotifications);
  const {
    setIsActive: setIsPushActive,
    isActive: isPushActive,
    hasError: hasPushError,
    handleChange: handlePushChange,
  } = useSettingApi(CommunicationMethodName.PUSH, updatePushNotifications);

  useEffect(() => {
    const getUserPreferences = async () => {
      try {
        const response = await fetchUserNotificationsPreferences();
        setIsPromotionsActive(response.promotionsEnabled);
        setIsResReviewActive(response.resReviewNotificationEnabled);
        setIsEmailsActive(response.emailNotificationsEnabled);
        setIsSmsActive(response.smsNotificationsEnabled);
        setIsPushActive(response.pushNotificationsEnabled);
        setIsLoaded(true);
      } catch (error) {
        if (is401Error(error)) {
          await handleRefreshToken(error, getUserPreferences);
        }
      }
    };

    getUserPreferences();
  }, [setIsPromotionsActive, setIsResReviewActive, setIsEmailsActive, setIsSmsActive, setIsPushActive]);

  const hasCheckboxesError = useMemo(() => hasEmailsError || hasPushError || hasSmsError,
    [hasEmailsError, hasPushError, hasSmsError]);

  return {
    isLoaded,
    hasCheckboxesError,
    settingControllers: {
      [ToggleName.PROMOTIONS]: {
        isActive: isPromotionsActive,
        hasError: hasPromotionsError,
        handleChange: handlePromotionsChange,
      },
      [ToggleName.RES_REVIEW]: {
        isActive: isResReviewActive,
        hasError: hasResReviewError,
        handleChange: handleResReviewChange,
      },
      [CommunicationMethodName.EMAIL]: {
        isActive: isEmailsActive,
        hasError: hasEmailsError,
        handleChange: handleEmailsChange,
      },
      [CommunicationMethodName.SMS]: {
        isActive: isSmsActive,
        hasError: hasSmsError,
        handleChange: handleSmsChange,
      },
      [CommunicationMethodName.PUSH]: {
        isActive: isPushActive,
        hasError: hasPushError,
        handleChange: handlePushChange,
      },
    },
  };
};

export default useSettings;
